<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useConfig } from 'dashboard/composables/useConfig';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';

import Copilot from 'dashboard/components-next/copilot/Copilot.vue';
import CopilotThreadsAPI from 'dashboard/api/captain/copilotThreads';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import wootConstants from 'dashboard/constants/globals';

defineProps({
  conversationInboxType: {
    type: String,
    default: '',
  },
});

const STATUS_POLL_INTERVAL = 2000;
const IDLE_STATUS_POLL_INTERVAL = 5000;

const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();
const { isEnterprise } = useConfig();
const { width: windowWidth } = useWindowSize();

const currentUser = useMapGetter('getCurrentUser');
const assistants = useMapGetter('captainAssistants/getRecords');
const inboxAssistant = useMapGetter('getCopilotAssistant');
const currentChat = useMapGetter('getSelectedChat');
const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const selectedCopilotThreadId = ref(null);
const currentThread = ref(null);
const selectedAssistantId = ref(null);
const isHistoryLoading = ref(true);
const isAssistantsLoading = ref(true);
const isControlPending = ref(false);

let statusTimer = null;
let hydrationVersion = 0;

const messages = computed(() =>
  store.getters['copilotMessages/getMessagesByThreadId'](
    selectedCopilotThreadId.value
  )
);

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const activeAssistant = computed(() => {
  const preferredId = uiSettings.value.preferred_captain_assistant_id;

  if (preferredId) {
    const preferredAssistant = assistants.value.find(a => a.id === preferredId);
    if (preferredAssistant) return preferredAssistant;
  }

  if (inboxAssistant.value) {
    const inboxMatchedAssistant = assistants.value.find(
      a => a.id === inboxAssistant.value.id
    );
    if (inboxMatchedAssistant) return inboxMatchedAssistant;
  }

  return assistants.value[0];
});

const isAgentRunning = computed(
  () => currentThread.value?.meta?.runtime_running === true
);
const isLoading = computed(
  () => isHistoryLoading.value || isAssistantsLoading.value
);

const shouldShowCopilotPanel = computed(() => {
  if (!isEnterprise) return false;

  const isCaptainEnabled = isFeatureEnabledonAccount.value(
    currentAccountId.value,
    FEATURE_FLAGS.CAPTAIN
  );
  return isCaptainEnabled && uiSettings.value.is_copilot_panel_open;
});

const sessionParams = () => {
  const conversationId = currentChat.value?.id;
  return conversationId ? { conversation_id: conversationId } : {};
};

const clearStatusTimer = () => {
  window.clearTimeout(statusTimer);
  statusTimer = null;
};

const fetchCurrentThread = async () => {
  const threads = await store.dispatch('copilotThreads/get', sessionParams());
  return threads?.[0] || null;
};

const loadCurrentMessages = async thread => {
  if (!thread?.id) return;
  await store.dispatch('copilotMessages/get', thread.id);
};

const statusPollDelay = () => {
  if (isAgentRunning.value) return STATUS_POLL_INTERVAL;

  const expiresAt = Number(currentThread.value?.meta?.session_expires_at);
  if (!Number.isFinite(expiresAt) || expiresAt <= 0) {
    return IDLE_STATUS_POLL_INTERVAL;
  }

  const untilRollover = expiresAt * 1000 - Date.now() + 250;
  return Math.max(250, Math.min(IDLE_STATUS_POLL_INTERVAL, untilRollover));
};

const scheduleStatusPoll = () => {
  clearStatusTimer();

  statusTimer = window.setTimeout(async () => {
    const previousThreadId = currentThread.value?.id;
    try {
      const thread = await fetchCurrentThread();
      const threadChanged = Boolean(
        previousThreadId && thread?.id && thread.id !== previousThreadId
      );
      currentThread.value = thread;
      selectedCopilotThreadId.value = thread?.id || null;
      if (threadChanged) await loadCurrentMessages(thread);
    } catch (error) {
      // A transient status failure must not hide the current conversation.
    } finally {
      scheduleStatusPoll();
    }
  }, statusPollDelay());
};

const hydrateHistory = async ({ showLoading = true } = {}) => {
  const version = ++hydrationVersion;
  if (showLoading) isHistoryLoading.value = true;

  try {
    const thread = await fetchCurrentThread();
    if (version !== hydrationVersion) return;

    currentThread.value = thread;
    selectedCopilotThreadId.value = thread?.id || null;
    await loadCurrentMessages(thread);
  } catch (error) {
    if (version === hydrationVersion) useAlert(error.message);
  } finally {
    if (version === hydrationVersion) {
      isHistoryLoading.value = false;
      scheduleStatusPoll();
    }
  }
};

const loadAssistants = async () => {
  try {
    await store.dispatch('captainAssistants/get');
  } catch (error) {
    useAlert(error.message);
  } finally {
    isAssistantsLoading.value = false;
  }
};

const markAgentRunning = () => {
  if (!currentThread.value) return;
  currentThread.value = {
    ...currentThread.value,
    meta: {
      ...currentThread.value.meta,
      runtime_running: true,
    },
  };
  scheduleStatusPoll();
};

const sendMessage = async message => {
  try {
    markAgentRunning();
    if (selectedCopilotThreadId.value) {
      const response = await store.dispatch('copilotMessages/create', {
        assistant_id: activeAssistant.value.id,
        conversation_id: currentChat.value?.id,
        threadId: selectedCopilotThreadId.value,
        message,
      });
      const responseThread = response?.copilot_thread;
      if (responseThread?.id) {
        currentThread.value = responseThread;
        selectedCopilotThreadId.value = responseThread.id;
      }
      markAgentRunning();
    } else {
      const response = await store.dispatch('copilotThreads/create', {
        assistant_id: activeAssistant.value.id,
        conversation_id: currentChat.value?.id,
        message,
      });
      currentThread.value = response;
      selectedCopilotThreadId.value = response.id;
      markAgentRunning();
    }
  } catch (error) {
    useAlert(error.message);
    await hydrateHistory({ showLoading: false });
  }
};

const handleAgentControl = async () => {
  if (isControlPending.value) return;

  isControlPending.value = true;
  try {
    if (isAgentRunning.value) {
      const response = await CopilotThreadsAPI.stop(sessionParams());
      currentThread.value = response.data?.payload?.[0] || currentThread.value;
      scheduleStatusPoll();
    } else {
      await hydrateHistory({ showLoading: false });
    }
  } catch (error) {
    useAlert(error.message);
  } finally {
    isControlPending.value = false;
  }
};

const closeCopilotPanel = () => {
  if (isSmallScreen.value && uiSettings.value?.is_copilot_panel_open) {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: false,
    });
  }
};

const setAssistant = async assistant => {
  selectedAssistantId.value = assistant.id;
  await updateUISettings({
    preferred_captain_assistant_id: assistant.id,
  });
};

const handleWindowFocus = async () => {
  const previousThreadId = currentThread.value?.id;
  try {
    const thread = await fetchCurrentThread();
    currentThread.value = thread;
    selectedCopilotThreadId.value = thread?.id || null;
    if (previousThreadId && thread?.id !== previousThreadId) {
      await loadCurrentMessages(thread);
    }
  } catch (error) {
    // Keep the visible session intact when a focus refresh fails.
  } finally {
    scheduleStatusPoll();
  }
};

onMounted(async () => {
  if (!isEnterprise || !isFeatureEnabledonAccount.value(currentAccountId.value, FEATURE_FLAGS.CAPTAIN)) return;
  window.addEventListener('focus', handleWindowFocus);
  await Promise.all([loadAssistants(), hydrateHistory()]);
});

onBeforeUnmount(() => {
  hydrationVersion += 1;
  clearStatusTimer();
  window.removeEventListener('focus', handleWindowFocus);
});
</script>

<template>
  <div
    v-if="shouldShowCopilotPanel"
    v-on-click-outside="() => closeCopilotPanel()"
    class="captain-bridge-panel bg-n-surface-2 h-full overflow-hidden flex-col fixed top-0 ltr:right-0 rtl:left-0 z-40 w-full transition-transform duration-300 ease-in-out md:static ltr:border-l rtl:border-r border-n-weak shadow-lg md:shadow-none"
    :class="[
      {
        'md:flex': shouldShowCopilotPanel,
        'md:hidden': !shouldShowCopilotPanel,
      },
    ]"
  >
    <Copilot
      :messages="messages"
      :support-agent="currentUser"
      :conversation-inbox-type="conversationInboxType"
      :assistants="assistants"
      :active-assistant="activeAssistant"
      :is-loading="isLoading"
      :is-agent-running="isAgentRunning"
      :is-control-pending="isControlPending"
      @set-assistant="setAssistant"
      @send-message="sendMessage"
      @control="handleAgentControl"
    />
  </div>
  <template v-else />
</template>

<style scoped>
@media (min-width: 768px) {
  .captain-bridge-panel {
    flex: 0 0 clamp(360px, 32vw, 600px);
    min-width: 360px;
    max-width: 600px;
    width: clamp(360px, 32vw, 600px);
  }
}
</style>
